import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Search } from "lucide-react";

interface ClienteResult {
  id: string;
  documento: string;
  nombre_completo: string;
  telefono: string | null;
  ciudad: string | null;
  direccion: string | null;
  email: string | null;
}

interface ClienteSearchProps {
  documento: string;
  onFound: (cliente: ClienteResult) => void;
}

/**
 * Busca cliente por documento al perder foco. Si lo encuentra, dispara onFound
 * con los datos para autocompletar el formulario.
 */
export function ClienteSearch({ documento, onFound }: ClienteSearchProps) {
  const [result, setResult] = useState<ClienteResult | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!documento || documento.length < 6) {
      setResult(null);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      const { data } = await supabase
        .from("clientes")
        .select("id, documento, nombre_completo, telefono, ciudad, direccion, email")
        .eq("documento", documento)
        .maybeSingle();
      setLoading(false);
      setResult(data as ClienteResult | null);
    }, 300);
    return () => clearTimeout(timer);
  }, [documento]);

  if (loading) {
    return (
      <p className="text-xs text-fg-muted flex items-center gap-1">
        <Search className="size-3 animate-pulse" /> Buscando...
      </p>
    );
  }

  if (!result) return null;

  return (
    <div className="card p-3 flex items-start gap-3 bg-brand/5 border-brand/30">
      <div className="flex-1">
        <p className="text-sm font-medium">{result.nombre_completo}</p>
        {result.telefono && <p className="text-xs text-fg-muted">{result.telefono}</p>}
      </div>
      <button
        type="button"
        onClick={() => onFound(result)}
        className="text-xs text-brand hover:underline whitespace-nowrap"
      >
        Usar este cliente
      </button>
    </div>
  );
}
